/** Shared state behind every bot memory view on screen: a short-lived summary
 * cache scoped to the signed-in account and workspace, and change notification
 * so the QuantisAI row and the admin panel never disagree. Only counts are
 * cached; note lists and note text are fetched per open view and never kept
 * here. Changes travel as a `bot-memory:changed` window event. */
import type { BotMemorySummary } from '../../../services/BotMemoryService';
import { currentScheduleActor } from './botSchedulesStore';

export const BOT_MEMORY_CHANGED_EVENT = 'bot-memory:changed';
export interface BotMemoryChangedDetail { workspaceId: string; origin: string }

const TTL_MS = 30_000;
const cache = new Map<string, { at: number; summary: BotMemorySummary; workspaceId: string }>();
/** Bumped on every change: a request that started before it is stale. */
const versions = new Map<string, number>();

// Sign-out clears the app's per-user caches with this event; do the same.
if (typeof window !== 'undefined') window.addEventListener('quantis:auth-cleared', () => cache.clear());

export const memoryCacheKey = (workspaceId: string) => `${currentScheduleActor()}|${workspaceId}`;
export const memoryVersion = (workspaceId: string) => versions.get(workspaceId) ?? 0;

export function readMemoryCache(key: string): BotMemorySummary | null {
  const hit = cache.get(key);
  return hit && Date.now() - hit.at < TTL_MS ? hit.summary : null;
}
export const writeMemoryCache = (key: string, workspaceId: string, summary: BotMemorySummary) => { cache.set(key, { at: Date.now(), summary, workspaceId }); };
export const clearBotMemoryCache = () => { cache.clear(); versions.clear(); };

/** Call after any note is saved, deleted or cleared. */
export function announceMemoryChanged(workspaceId: string, origin: string): void {
  versions.set(workspaceId, memoryVersion(workspaceId) + 1);
  for (const [key, entry] of cache) if (entry.workspaceId === workspaceId) cache.delete(key);
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent<BotMemoryChangedDetail>(BOT_MEMORY_CHANGED_EVENT, { detail: { workspaceId, origin } }));
}
